import { AxiosResponse } from "axios"
import { apiInstance } from "../base"
import {
    IAddRestaurantMenuImageUrlPayload,
    ICreateRestaurantMenuPayload,
    IFullRestaurantMenu,
    IIikoCatalogOrganizationsResponse,
    IIikoCatalogTerminalGroupsResponse,
    IListRestaurantMenusResponse,
    IRestaurantMenu,
    IRestaurantMenuItemImage,
    IRestaurantMenuItemOverride,
    IRestaurantMenuItemTranslation,
    IRestaurantMenuLanguage,
    ISetRestaurantMenuLanguagesPayload,
    ISetRestaurantMenuTranslationPayload,
    IUpdateRestaurantMenuAvailabilityPayload,
    IUpdateRestaurantMenuPayload,
    MenuEntityType,
} from "./types"

export const listRestaurantMenus = (): Promise<AxiosResponse<IListRestaurantMenusResponse>> => {
    return apiInstance.handler.get<IListRestaurantMenusResponse>("/restaurant-menus")
}

export const getIikoCatalogOrganizations = (
    apiLogin: string
): Promise<AxiosResponse<IIikoCatalogOrganizationsResponse>> => {
    return apiInstance.handler.get<IIikoCatalogOrganizationsResponse>("/restaurant-menus/iiko-catalog/organizations", {
        params: {
            api_login: apiLogin,
        },
    })
}

export const getIikoCatalogTerminalGroups = (
    apiLogin: string,
    organizationId: string
): Promise<AxiosResponse<IIikoCatalogTerminalGroupsResponse>> => {
    return apiInstance.handler.get<IIikoCatalogTerminalGroupsResponse>(
        "/restaurant-menus/iiko-catalog/terminal-groups",
        {
            params: {
                api_login: apiLogin,
                organization_id: organizationId,
            },
        }
    )
}

export const createRestaurantMenu = (data: ICreateRestaurantMenuPayload): Promise<AxiosResponse<IRestaurantMenu>> => {
    return apiInstance.handler.post<IRestaurantMenu>("/restaurant-menus", data)
}

export const getRestaurantMenu = (menuId: string): Promise<AxiosResponse<IRestaurantMenu>> => {
    return apiInstance.handler.get<IRestaurantMenu>(`/restaurant-menus/${menuId}`)
}

export const getFullRestaurantMenu = (menuId: string): Promise<AxiosResponse<IFullRestaurantMenu>> => {
    return apiInstance.handler.get<IFullRestaurantMenu>(`/restaurant-menus/${menuId}/full`)
}

export const updateRestaurantMenu = (
    menuId: string,
    data: IUpdateRestaurantMenuPayload
): Promise<AxiosResponse<IRestaurantMenu>> => {
    return apiInstance.handler.put<IRestaurantMenu>(`/restaurant-menus/${menuId}`, data)
}

export const deleteRestaurantMenu = (menuId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete<void>(`/restaurant-menus/${menuId}`)
}

export const getRestaurantMenuOverrides = (menuId: string): Promise<AxiosResponse<IRestaurantMenuItemOverride[]>> => {
    return apiInstance.handler.get<IRestaurantMenuItemOverride[]>(`/restaurant-menus/${menuId}/overrides`)
}

export const deleteRestaurantMenuOverride = (
    menuId: string,
    entityType: MenuEntityType,
    itemId: string
): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete<void>(`/restaurant-menus/${menuId}/overrides/${entityType}/${itemId}`)
}

export const updateRestaurantMenuItemAvailability = (
    menuId: string,
    itemId: string,
    data: IUpdateRestaurantMenuAvailabilityPayload
): Promise<AxiosResponse<IRestaurantMenuItemOverride>> => {
    return apiInstance.handler.patch<IRestaurantMenuItemOverride>(
        `/restaurant-menus/${menuId}/items/${itemId}/availability`,
        data
    )
}

export const updateRestaurantMenuModifierAvailability = (
    menuId: string,
    itemId: string,
    data: IUpdateRestaurantMenuAvailabilityPayload
): Promise<AxiosResponse<IRestaurantMenuItemOverride>> => {
    return apiInstance.handler.patch<IRestaurantMenuItemOverride>(
        `/restaurant-menus/${menuId}/modifiers/${itemId}/availability`,
        data
    )
}

export const getRestaurantMenuItemImages = (
    menuId: string,
    itemId: string
): Promise<AxiosResponse<IRestaurantMenuItemImage[]>> => {
    return apiInstance.handler.get<IRestaurantMenuItemImage[]>(`/restaurant-menus/${menuId}/items/${itemId}/images`)
}

export const reorderRestaurantMenuItemImages = (
    menuId: string,
    itemId: string,
    imageIds: string[]
): Promise<AxiosResponse<IRestaurantMenuItemImage[]>> => {
    return apiInstance.handler.put<IRestaurantMenuItemImage[]>(
        `/restaurant-menus/${menuId}/items/${itemId}/images/order`,
        {
            image_ids: imageIds,
        }
    )
}

export const uploadRestaurantMenuItemImage = (
    menuId: string,
    itemId: string,
    image: File,
    sortOrder: number
): Promise<AxiosResponse<IRestaurantMenuItemImage>> => {
    const formData = new FormData()
    formData.append("image", image)
    formData.append("sort_order", String(sortOrder))

    return apiInstance.handler.post<IRestaurantMenuItemImage>(
        `/restaurant-menus/${menuId}/items/${itemId}/images`,
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        }
    )
}

export const addRestaurantMenuItemImageUrl = (
    menuId: string,
    itemId: string,
    data: IAddRestaurantMenuImageUrlPayload
): Promise<AxiosResponse<IRestaurantMenuItemImage>> => {
    return apiInstance.handler.post<IRestaurantMenuItemImage>(
        `/restaurant-menus/${menuId}/items/${itemId}/images/url`,
        data
    )
}

export const deleteRestaurantMenuImage = (menuId: string, imageId: string): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete<void>(`/restaurant-menus/${menuId}/images/${imageId}`)
}

export const getRestaurantMenuLanguages = (menuId: string): Promise<AxiosResponse<IRestaurantMenuLanguage[]>> => {
    return apiInstance.handler.get<IRestaurantMenuLanguage[]>(`/restaurant-menus/${menuId}/languages`)
}

export const setRestaurantMenuLanguages = (
    menuId: string,
    data: ISetRestaurantMenuLanguagesPayload
): Promise<AxiosResponse<IRestaurantMenuLanguage[]>> => {
    return apiInstance.handler.put<IRestaurantMenuLanguage[]>(`/restaurant-menus/${menuId}/languages`, data)
}

export const getRestaurantMenuTranslations = (
    menuId: string
): Promise<AxiosResponse<IRestaurantMenuItemTranslation[]>> => {
    return apiInstance.handler.get<IRestaurantMenuItemTranslation[]>(`/restaurant-menus/${menuId}/translations`)
}

export const setRestaurantMenuTranslation = (
    menuId: string,
    entityType: MenuEntityType,
    entityId: string,
    data: ISetRestaurantMenuTranslationPayload
): Promise<AxiosResponse<IRestaurantMenuItemTranslation>> => {
    return apiInstance.handler.put<IRestaurantMenuItemTranslation>(
        `/restaurant-menus/${menuId}/translations/${entityType}/${entityId}`,
        data
    )
}

export const deleteRestaurantMenuTranslation = (
    menuId: string,
    entityType: MenuEntityType,
    entityId: string,
    languageCode: string
): Promise<AxiosResponse<void>> => {
    return apiInstance.handler.delete<void>(
        `/restaurant-menus/${menuId}/translations/${entityType}/${entityId}/${languageCode}`
    )
}
